import React, {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  Checkbox,
  CloseIcon,
  Column,
  FilterIcon,
  IconButton,
  Menu,
  MenuActionsButton,
  MenuButton,
  MenuItem,
  MenuList,
  Badge,
  Portal,
  Row,
  SearchInput,
  Text,
  Tooltip,
  useToast,
  SourceIcon,
  TextWithTooltip,
  HtImage,
} from "@hightouch/htui";
import { isEqual, noop, orderBy, uniqBy } from "lodash";
import { useQueryClient } from "react-query";
import { captureException } from "src/lib/monitoring";
import { useLocation, useSearchParams } from "src/router";
import { isPresent } from "ts-extras";

import {
  type ModelFiltersQuery,
  useCreateViewMutation,
  useDeleteViewMutation,
  useUpdateViewMutation,
  useViewsQuery,
  type ViewsQuery,
} from "src/graphql";
import { parseCommaList } from "src/utils/comma-list";
import { QueryTypeDictionary } from "src/utils/models";
import { commaNumber } from "src/utils/numbers";
import { load, remove, save } from "src/utils/storage";
import {
  SyncHealthStatus,
  SyncRunStatus,
  SyncStatusToText,
} from "src/utils/syncs";

import {
  type Filter as ViewFilter,
  FilterOperator,
  type FilterType,
} from "src/components/filter";
import { CreateViewModal } from "src/components/filter/create-view";
import { SyncRunStatusBadge } from "src/components/syncs/sync-run-status-badge";
import { IntegrationIcon } from "src/components/integrations/integration-icon";
import { QueryType } from "src/types/models";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import { CollapseChevron } from "src/components/folders/collapse-chevron";
import { RowButton } from "src/components/folders/row-button";
import { PrivacyLevel } from "@hightouch/lib/query/privacy-utils";
import { piiCategories } from "src/components/privacy/pii-categories";

export type FilterState = { selected: FilterOption[]; isAllSelected: boolean };

export type FilterData = CollapsableFilterProps & {
  section?: string;
};

/**
 * Rail order: dimensions that open by default first, alphabetical by label within
 * each group. Ties keep definition order, since `Array.prototype.sort` is stable.
 */
export const byDefaultOpenThenTitle = (
  a: { title: string; isDefaultOpen?: boolean },
  b: { title: string; isDefaultOpen?: boolean },
) =>
  Number(b.isDefaultOpen ?? false) - Number(a.isDefaultOpen ?? false) ||
  a.title.localeCompare(b.title);

/**
 * `section` groups dimensions in the rail; it is not a CollapsableFilter prop,
 * so it is stripped here rather than at every call site.
 */
const renderFilter = ({ section: _section, ...filter }: FilterData) => (
  <CollapsableFilter key={filter.filterKey ?? filter.title} {...filter} />
);

/** One column per section, headed by the section name. */
const FilterSections: FC<{ filters: FilterData[] }> = ({ filters }) => {
  const sections = [
    ...new Set(filters.map((filter) => filter.section).filter(isPresent)),
  ];

  return (
    <>
      {sections.map((section) => (
        <Column key={section}>
          <Box mb={2}>
            <Text color="text.secondary" fontWeight="semibold" size="sm">
              {section}
            </Text>
          </Box>
          {filters
            .filter((filter) => filter.section === section)
            .map(renderFilter)}
        </Column>
      ))}
    </>
  );
};

const isUnrestrictedSelection = ({
  selectedOptions,
  options,
  selectionMode,
}: Pick<FilterData, "selectedOptions" | "options" | "selectionMode">) =>
  selectionMode === "explicit"
    ? selectedOptions.length === 0
    : selectedOptions.length === options.length;

/**
 * The canonical form of a selection: dimensions in definition order, values in
 * option order, ids resolved against the options that exist now, and any
 * dimension that ends up unrestricted dropped. Both a stored view and the live
 * URL selection pass through here before they are compared, because neither
 * arrives canonical: `setFilters` writes against every option while
 * `getFilterQueryState` reads back active ones only, and the URL carries values
 * in whatever order they were typed.
 *
 * Expects the translated, `_in`-only form held in `views`.
 */
export function canonicalizeSelection(
  selection: ViewFilter[],
  filters: FiltersDefinition,
): ViewFilter[] {
  const canonical: ViewFilter[] = [];

  for (const [filterKey, definition] of Object.entries(filters)) {
    const entry = selection.find((filter) => filter.type === filterKey);
    if (!entry) continue;

    const options = definition.options.filter(
      (o): o is FilterOption => !o.disabled,
    );
    const wanted = new Set(
      (entry.value ?? []).map((id: unknown) => String(id)),
    );
    const selectedOptions = options.filter((o) => wanted.has(String(o.id)));

    if (selectedOptions.length === 0) continue;
    if (
      isUnrestrictedSelection({
        selectedOptions,
        options,
        selectionMode: definition.selectionMode,
      })
    ) {
      continue;
    }

    canonical.push({
      type: filterKey as FilterType,
      operator: FilterOperator.Includes,
      value: selectedOptions.map((o) => o.id),
    });
  }

  return canonical;
}

export type FiltersDefinition = { [key: string]: Filter };

export type FiltersState<F extends FiltersDefinition> = {
  [key in keyof F]: FilterState;
};

export type FiltersResult<F extends FiltersDefinition> = {
  state: FiltersState<F>;
  data: FilterData[];
};

export function useFilters<F extends FiltersDefinition>({
  filters,
  viewKey,
  loading,
  resource,
}: {
  filters: F;
  viewKey: string;
  loading: boolean;
  /**
   * Which rail this feeds, if any. Names the built-in view rows `Filters`
   * prepends, so `?view=default` restores instead of being dropped as an unknown
   * id, and so selecting one is not remembered the way a saved view is.
   */
  resource?: FilterResource;
}): {
  result: FiltersResult<F>;
  state: {
    creatingView: boolean;
    selectedView: string | null;
    viewNotSaved: boolean;
    views: ViewsQuery["resource_views"];
    updatingView: boolean;
  };
  actions: {
    createView: (args: { name: string; private?: boolean }) => Promise<void>;
    deleteView: (args: { id: string }) => Promise<void>;
    selectView: (args: { view: string; filters: ViewFilter[] }) => void;
    updateCurrentView: () => Promise<void>;
    resetViewFilters: () => void;
    clearFilters: () => void;
  };
} {
  const localStorageKey = `${viewKey}-view`;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const restored = useRef(false);
  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { mutateAsync: createNewView, isLoading: creatingView } =
    useCreateViewMutation();
  const { mutateAsync: updateSavedView, isLoading: updatingView } =
    useUpdateViewMutation();
  const { mutateAsync: deleteSavedView } = useDeleteViewMutation();

  const { data: viewsData, isFetched: viewsFetched } = useViewsQuery({
    resourceType: viewKey,
  });

  const translateBackendFilters = useCallback(
    (newFilter: ViewsQuery["resource_views"][0]["view"]) => {
      const currentFilter = filters[newFilter.type]!;
      if (!currentFilter) {
        return null;
      }
      if (newFilter.operator === FilterOperator.Excludes) {
        return {
          operator: FilterOperator.Includes,
          type: newFilter.type,
          value: currentFilter.options
            .filter((o) => !newFilter.value.includes(o.id))
            .map((o) => o.id),
        };
      }
      return newFilter;
    },
    [filters],
  );

  const views = useMemo(
    () =>
      (viewsData?.resource_views ?? [])
        .filter((view) => {
          // We don't support views that filter on label keys only.
          return view.view.every(
            (v) =>
              v.operator === FilterOperator.Includes ||
              v.operator === FilterOperator.Excludes,
          );
        })
        .map((view) => {
          return {
            ...view,
            view: view.view.map(translateBackendFilters).filter(Boolean),
          };
        }),
    [viewsData?.resource_views, translateBackendFilters],
  );

  const builtIns = useMemo(() => builtInViewsFor(resource), [resource]);
  /** Built-ins plus server rows: every id a `?view=` param may legitimately name. */
  const selectableViews = useMemo(
    () => [...builtIns, ...views],
    [builtIns, views],
  );

  const urlView = searchParams.get("view");
  // The URL is the source of truth for which view is selected. An id we cannot
  // resolve is never adopted: it could not be an update target.
  const selectedView =
    urlView && selectableViews.some((view) => String(view.id) === urlView)
      ? urlView
      : null;

  /**
   * The one writer for the filter query params: a fresh instance, committed in
   * a single navigation. `view: undefined` leaves the param alone and `null`
   * removes it; omitting `filters` leaves the dimension params alone, which is
   * how creating a view adopts an id without disturbing the current slice.
   */
  const commit = useCallback(
    ({
      view,
      filters: newFilters,
      replace = false,
    }: {
      view?: string | null;
      filters?: ViewFilter[];
      replace?: boolean;
    }) => {
      // The updater form, so the new URL is built from the params as they are
      // at write time rather than as they were when this callback was created.
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);

          if (view !== undefined) {
            if (view) {
              params.set("view", view);
            } else {
              params.delete("view");
            }
          }

          if (newFilters) {
            for (const key of Object.keys(filters)) {
              params.delete(key);
            }

            for (const filter of newFilters) {
              const currentFilter = filters[filter.type];
              if (!currentFilter) {
                continue;
              }

              if (filter.operator === FilterOperator.Includes) {
                const optionsToSet = filter.value
                  .map((id) =>
                    currentFilter.options.find(
                      (o) => o?.id?.toString() === id.toString(),
                    ),
                  )
                  .filter((o): o is FilterOption => Boolean(o));
                params.set(
                  filter.type,
                  optionsToSet.map((o) => o.id).join(","),
                );
              } else if (filter.operator === FilterOperator.Excludes) {
                const optionsToSet = currentFilter.options.filter(
                  (o) => !filter.value.includes(o.id),
                );
                params.set(
                  filter.type,
                  optionsToSet.map((o) => o.id).join(","),
                );
              }
            }
          }

          return params;
        },
        { replace, state: location.state },
      );
    },
    [filters, setSearchParams, location.state],
  );

  /**
   * Apply a view. Its id and its filters land in one navigation: a render that
   * carries the id but not yet the filters reads as unsaved changes. Guarded on
   * readiness, because the filters resolve ids against the loaded options.
   */
  const applyView = useCallback(
    (viewId: string | null, viewFilters: ViewFilter[], replace = false) => {
      if (loading) return;
      // Only a saved view is remembered; a built-in is a preset.
      if (viewId && !builtIns.some((v) => v.id === viewId)) {
        save(localStorageKey, viewId);
      } else {
        remove(localStorageKey);
      }
      commit({ view: viewId, filters: viewFilters, replace });
    },
    [builtIns, commit, loading, localStorageKey],
  );

  const hasFilterParams = Object.keys(filters).some((key) =>
    searchParams.has(key),
  );

  // Restore once the view rows and the dimension definitions are both in: ids in
  // the URL or in storage can only be resolved against loaded options. Latched,
  // because this seeds the URL from a link or from storage exactly once — after
  // that the user's own edits own it, and re-running would fight them when
  // unchecking the last dimension empties the params again.
  useEffect(() => {
    if (restored.current || loading || !viewsData) return;
    restored.current = true;

    if (urlView) {
      const view = selectableViews.find((v) => String(v.id) === urlView);
      if (!view) {
        // Storage is left alone: an unresolvable id in the URL must not wipe a
        // perfectly good remembered one.
        commit({ view: null, replace: true });
        return;
      }
      // Explicit filter params win — the link asked for that exact slice — but
      // the view is still adopted, so Save targets it.
      // Replaced, not pushed: hydrating a link is not a step the user took, and
      // Back would otherwise land on this URL with the view still named but its
      // filters gone, which the latch above stops us from re-applying.
      if (!hasFilterParams) applyView(urlView, view.view, true);
      return;
    }

    if (hasFilterParams) return;

    const remembered = load(localStorageKey);
    // Saved rows only, matching what applyView stores, so a built-in id left in storage stays inert.
    const rememberedView =
      remembered == null
        ? undefined
        : views.find((v) => String(v.id) === String(remembered));
    if (rememberedView) {
      applyView(String(rememberedView.id), rememberedView.view, true);
    }
  }, [
    applyView,
    commit,
    hasFilterParams,
    loading,
    localStorageKey,
    selectableViews,
    urlView,
    views,
    viewsData,
  ]);

  const getFilterQueryState = useCallback(
    (
      filterKey: string,
      filterOptions: FilterOption[],
      selectionMode?: "explicit",
    ) =>
      () => {
        const selectedOptions = searchParams.get(filterKey);

        if (!selectedOptions || selectedOptions.length === 0) {
          return selectionMode === "explicit" ? [] : filterOptions;
        }

        return parseCommaList(selectedOptions)
          .map((id) => {
            const option = filterOptions.find(
              (o) => o?.id?.toString() === id?.toString(),
            );
            if (!option) {
              return null;
            }
            return option;
          })
          .filter((o: FilterOption | null): o is FilterOption => Boolean(o));
      },
    [searchParams],
  );

  const setFilterQueryState = useCallback(
    (
      filterKey: string,
      filterOptions: FilterOption[],
      selectionMode?: "explicit",
    ) =>
      (selectedOptions: FilterOption[]) => {
        if (
          isUnrestrictedSelection({
            selectedOptions,
            options: filterOptions,
            selectionMode,
          })
        ) {
          searchParams.delete(filterKey);
          setSearchParams(searchParams, { replace: true });
          return;
        }

        searchParams.set(filterKey, selectedOptions.map((o) => o.id).join(","));
        setSearchParams(searchParams, { replace: true });
      },
    [searchParams, setSearchParams],
  );

  const resetViewFilters = useCallback(() => {
    // selectableViews, not views: Reset has to work for a built-in too.
    const view = selectableViews.find((v) => String(v.id) === selectedView);
    if (view && !loading) commit({ filters: view.view });
  }, [commit, loading, selectableViews, selectedView]);

  /**
   * Drop the selection from the URL, from storage, and from every dimension
   * param. Unguarded, unlike the two above: clearing resolves no ids, so it has
   * nothing to wait for.
   */
  const clearFilters = useCallback(() => {
    remove(localStorageKey);
    commit({ view: null, filters: [] });
  }, [commit, localStorageKey]);

  // triggers when the query params change to calculate the current filters state.
  const result: FiltersResult<F> = useMemo(() => {
    const data: FilterData[] = [];
    const state = Object.fromEntries(
      Object.keys(filters).map((key): [string, FilterState] => [
        key,
        { selected: [], isAllSelected: false },
      ]),
    );
    if (!loading) {
      for (const [
        filterKey,
        {
          options,
          title,
          canSearch,
          isDefaultOpen,
          preserveTitleCase,
          section,
          selectionMode,
        },
      ] of Object.entries(filters)) {
        const activeOptions: FilterOption[] = options.filter(
          (o): o is FilterOption => !o.disabled,
        );
        const value = getFilterQueryState(
          filterKey,
          activeOptions,
          selectionMode,
        )();
        state[filterKey] = {
          selected: value,
          isAllSelected: isUnrestrictedSelection({
            selectedOptions: value,
            options: activeOptions,
            selectionMode,
          }),
        };

        const filter: FilterData = {
          selectedOptions: value,
          setSelectedOptions: setFilterQueryState(
            filterKey,
            activeOptions,
            selectionMode,
          ),
          options,
          title,
          canSearch,
          isDefaultOpen,
          filterKey,
          preserveTitleCase,
          section,
          selectionMode,
        };
        data.push(filter);
      }
    }
    return { state: state as FiltersState<F>, data };
  }, [filters, loading, getFilterQueryState, setFilterQueryState]);

  const currentFilterObjects = useMemo(() => {
    const filterObjects: ViewFilter[] = [];
    for (const [filterKey, { selected, isAllSelected }] of Object.entries(
      result.state,
    )) {
      if (isAllSelected) {
        continue;
      }

      filterObjects.push({
        type: filterKey as FilterType,
        operator: FilterOperator.Includes,
        value: selected.map((o) => o.id),
      });
    }

    return filterObjects;
  }, [result.state]);

  /** The selection the selected view stands for; empty when none is selected. */
  const savedSelection = useMemo(() => {
    const view = selectableViews.find((v) => String(v.id) === selectedView);
    return view ? canonicalizeSelection(view.view, filters) : [];
  }, [filters, selectableViews, selectedView]);

  const currentSelection = useMemo(
    () => canonicalizeSelection(currentFilterObjects, filters),
    [currentFilterObjects, filters],
  );

  // Derived, not latched. Gated on the views query having settled rather than
  // on it having rows: before it does we cannot tell a modified view from an
  // unrecognised one, but a query that failed must still leave "Save as"
  // reachable for the filters the user has picked.
  const viewNotSaved =
    !loading && viewsFetched && !isEqual(currentSelection, savedSelection);

  /**
   * Put a row we just saved into the Views cache, so the rail reflects it in the
   * render after the mutation instead of reading as unsaved until the refetch
   * lands. The refetch stays authoritative and overwrites this.
   */
  const cacheView = useCallback(
    (row: ViewsQuery["resource_views"][0]) => {
      queryClient.setQueryData<ViewsQuery>(
        useViewsQuery.getKey({ resourceType: viewKey }),
        (current) => {
          const others = (current?.resource_views ?? []).filter(
            (v) => String(v.id) !== String(row.id),
          );
          return {
            resource_views: orderBy([...others, row], ["name"], ["desc"]),
          };
        },
      );
    },
    [queryClient, viewKey],
  );

  const createView = useCallback(
    async ({
      name,
      private: privateView,
    }: {
      name: string;
      private?: boolean;
    }) => {
      const view = currentFilterObjects;
      try {
        const res = await createNewView({
          resourceType: viewKey,
          name,
          private: Boolean(privateView),
          view,
        });
        const id = res.insert_resource_views_one?.id;
        if (!id) {
          // Hasura returns a null row when select permissions hide it. Closing
          // the dialog here would report success for a view nobody can use.
          throw new Error(
            "Something went wrong on our end. Try again, or contact support if it keeps happening.",
          );
        }
        // The mutation returns the id alone; the rest is what we just sent.
        cacheView({ id, name, view });
        save(localStorageKey, String(id));
        commit({ view: String(id), replace: true });
      } catch (error) {
        captureException(error);
        toast({
          id: "create-view",
          title: "View couldn't be created",
          message: error.message,
          variant: "error",
        });
        // Rethrow so the dialog stays open with what the user typed. This toast
        // is the only error surface — the dialog must not add a second one.
        throw error;
      }
    },
    [
      cacheView,
      commit,
      createNewView,
      currentFilterObjects,
      localStorageKey,
      toast,
      viewKey,
    ],
  );

  const updateCurrentView = useCallback(async () => {
    // `views` is server rows only, so a built-in or an unresolvable id finds
    // nothing and there is nothing to update. The rail does not offer Save for
    // those; this is the backstop for consumers that build their own rail, and
    // for a hand-typed `?view=default`.
    const target = views.find((v) => String(v.id) === selectedView);
    if (!target) return;

    const view = currentFilterObjects;
    try {
      await updateSavedView({ id: target.id, view });
      cacheView({ ...target, view });
      toast({
        id: "save-view",
        title: "View saved",
        variant: "success",
      });
    } catch (error) {
      captureException(error);
      toast({
        id: "save-view",
        title: "View couldn't be saved",
        message: error.message,
        variant: "error",
      });
    }
  }, [
    cacheView,
    currentFilterObjects,
    selectedView,
    toast,
    updateSavedView,
    views,
  ]);

  const deleteView = useCallback(
    async (args: { id: string }) => {
      try {
        await deleteSavedView(args);
        // Only the current selection goes: deleting some other view must not
        // clear the list the user is looking at.
        if (String(args.id) === selectedView) clearFilters();

        toast({
          id: "delete-view",
          title: "View deleted",
          variant: "success",
        });
      } catch (error) {
        captureException(error);
        toast({
          id: "delete-view",
          title: "View couldn't be deleted",
          message: error.message,
          variant: "error",
        });
      }
    },
    [clearFilters, deleteSavedView, selectedView, toast],
  );

  const state = useMemo(() => {
    return {
      creatingView,
      selectedView,
      viewNotSaved,
      views,
      updatingView,
    };
  }, [creatingView, selectedView, viewNotSaved, views, updatingView]);

  const actions = useMemo(() => {
    return {
      createView,
      deleteView,
      selectView: ({
        view,
        filters,
      }: {
        view: string;
        filters: ViewFilter[];
      }) => applyView(view, filters),
      updateCurrentView,
      resetViewFilters,
      clearFilters,
    };
  }, [
    applyView,
    createView,
    deleteView,
    updateCurrentView,
    resetViewFilters,
    clearFilters,
  ]);

  return {
    result,
    state,
    actions,
  };
}

export type FilterResource =
  | "audience"
  | "audience_schema"
  | "campaign"
  | "condition_template"
  | "content_block"
  | "destination"
  | "email_template"
  | "message"
  | "model"
  | "source"
  | "sync"
  | "whatsapp_template";

export interface FilterProps {
  filters: FilterData[];
  resource: FilterResource;
  resetFilters: () => void;
  clearFilters: () => void;
  viewNotSaved: boolean;
  selectedView: string | null;
  updatingView: boolean;
  updateCurrentView: () => void;
  views: ViewsQuery["resource_views"];
  selectView: (view: { view: string; filters: ViewFilter[] }) => void;
  deleteView: ({ id }: { id: string }) => void;
  creatingView: boolean;
  createView: (data: { name: string; private: boolean }) => Promise<void>;
}

interface IndividualViewProps {
  name: string;
  id: string;
  selectedView: string | null;
  selectView: (view: { view: string; filters: ViewFilter[] }) => void;
  deleteView: ({ id }: { id: string }) => void;
  views: {
    name: string;
    id: string | null;
    view?: ViewsQuery["resource_views"][0]["view"];
  }[];
}

const permanentViews = ["default", "currently-running-syncs"];

const IndividualView: FC<IndividualViewProps> = ({
  views,
  name,
  id,
  selectedView,
  selectView,
  deleteView,
}) => {
  const onClick = () => {
    if (id === null) {
      selectView({ view: id, filters: [] });
      return;
    }

    const selectedView = views.find((v) => v.id === id);

    if (selectedView) {
      selectView({ view: id, filters: selectedView.view });
    }
  };

  // Server ids arrive as bigints, so compare as strings against the URL value.
  const isSelected: boolean =
    selectedView !== null && String(id) === selectedView;
  const isPermanent = permanentViews.includes(String(id));

  return (
    <Row
      className="group"
      align="center"
      borderRadius="xl"
      bg={undefined}
      borderWidth="hairline"
      borderStyle="solid"
      borderColor="transparent"
      cursor="pointer"
      fontWeight="medium"
      gap={2}
      height="32px"
      justify="space-between"
      px={2}
      py={1}
      position="relative"
      width="100%"
      _hover={{
        bg: "base.lightBackground",
        borderColor: "base.border",
        boxShadow: "buttonShadowLight",
        cursor: "pointer",
      }}
      _groupHover={{
        bg: "base.lightBackground",
        borderColor: "base.border",
        boxShadow: "buttonShadowLight",
        cursor: "pointer",
      }}
      // Intentional sx: no pseudo prop for :has(:focus-visible)
      sx={{
        "&:has(:focus-visible)": {
          bg: "base.lightBackground",
          borderColor: "base.border",
          boxShadow: "buttonShadowLight",
          ".action-button": {
            opacity: 1,
          },
        },
      }}
    >
      <RowButton
        overlayRadius="xl"
        align="center"
        aria-current={isSelected ? "true" : undefined}
        display="grid"
        gridTemplateColumns="min-content 1fr"
        gap={1}
        onClick={onClick}
      >
        <Box
          as={FilterIcon}
          color={isSelected ? "primary.base" : "text.tertiary"}
          fontSize="16px"
          height="16px"
          width="16px"
        />
        {/* Lift above the row button's ::after so the tooltip still gets hover */}
        <Box minWidth={0} overflow="hidden" position="relative" zIndex={1}>
          <TextWithTooltip
            size="sm"
            color={isSelected ? "primary.base" : "text.secondary"}
            fontWeight="medium"
          >
            {name}
          </TextWithTooltip>
        </Box>
      </RowButton>
      {!isPermanent && (
        <Box
          position="absolute"
          right={1}
          top="50%"
          transform="translateY(-50%)"
          onClick={(event) => event.stopPropagation()}
          className="action-button"
          opacity={0}
          _groupHover={{
            opacity: 1,
          }}
          // Keep visible while the menu is open (focus leaves the row on open)
          sx={{
            "&:has([aria-expanded='true'])": {
              opacity: 1,
            },
          }}
        >
          <Menu>
            <MenuActionsButton size="sm" />
            <Portal>
              <MenuList>
                <MenuItem
                  variant="danger"
                  onClick={() => {
                    deleteView({ id });
                  }}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Box>
      )}
    </Row>
  );
};

export const Filters: FC<FilterProps> = ({
  filters,
  resetFilters,
  viewNotSaved,
  clearFilters,
  selectedView,
  updatingView,
  updateCurrentView,
  views,
  selectView,
  deleteView,
  createView,
  creatingView,
  resource,
}) => {
  const [createViewModalOpen, setCreateViewModalOpen] = useState(false);

  const allViews = [...builtInViewsFor(resource), ...views];

  const filterCount = filters.filter(
    (filter) => !isUnrestrictedSelection(filter),
  ).length;
  const canClear = filterCount > 0;
  // A built-in has no row to update, so it never offers Save — only Save as.
  const canUpdateSelectedView =
    selectedView !== null && !permanentViews.includes(selectedView);

  return (
    <Column gap={4}>
      <Column>
        <Box mb={2}>
          <Text color="text.secondary" fontWeight="semibold" size="sm">
            Saved filters
          </Text>
        </Box>
        <Column>
          {allViews.map((view) => {
            return (
              <IndividualView
                key={view.id}
                deleteView={deleteView}
                id={view.id}
                name={view.name}
                selectView={selectView}
                selectedView={selectedView}
                views={allViews}
              />
            );
          })}
        </Column>
      </Column>

      <Column>
        <Row align="center" justify="space-between" mb={2}>
          <Row alignItems="center" color="text.secondary" gap={2} height={6}>
            <Text fontWeight="semibold" size="sm" color="text.secondary">
              Filter by
            </Text>
            {filterCount > 0 && (
              <Badge variant="info" size="sm">
                {filterCount}
              </Badge>
            )}
          </Row>
          <Row gap={1}>
            {canClear && (
              <>
                {viewNotSaved &&
                  (!canUpdateSelectedView ? (
                    <Button
                      size="sm"
                      onClick={() => {
                        setCreateViewModalOpen(true);
                      }}
                    >
                      Save as
                    </Button>
                  ) : (
                    <Menu>
                      <MenuButton size="sm">Save</MenuButton>

                      <MenuList>
                        <MenuItem
                          isDisabled={updatingView}
                          onClick={() => {
                            updateCurrentView();
                          }}
                        >
                          Save
                        </MenuItem>
                        <MenuItem
                          isDisabled={creatingView}
                          onClick={() => {
                            setCreateViewModalOpen(true);
                          }}
                        >
                          Save as
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            resetFilters();
                          }}
                        >
                          Reset filters
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  ))}
                <Tooltip message="Clear filters">
                  <IconButton
                    size="sm"
                    variant="secondary"
                    icon={CloseIcon}
                    aria-label="Clear filters"
                    mr={1}
                    onClick={() => {
                      clearFilters();
                    }}
                  />
                </Tooltip>
              </>
            )}
          </Row>
        </Row>

        {filters
          .filter((filter) => !filter.section)
          .sort(byDefaultOpenThenTitle)
          .map(renderFilter)}

        <CreateViewModal
          isOpen={createViewModalOpen}
          loading={creatingView}
          onClose={() => setCreateViewModalOpen(false)}
          onSave={createView}
        />
      </Column>

      <FilterSections filters={filters} />
    </Column>
  );
};

export interface FilterOption {
  id: string;
  label?: string;
  icon?: ReactNode;
  badge?: ReactNode;
  // Matched by the section search in addition to label and id
  searchValue?: string;
  count?: number;
  filteredCount?: number;
  order?: number;
  disabled?: false;
}

export interface DisabledFilterOption {
  id: string;
  label: string;
  disabled: true;
  badge?: string;
  order?: number;
}

export type FilterOptionOrDisabled = FilterOption | DisabledFilterOption;

interface CollapsableFilterProps {
  filterKey?: string;
  title: string;
  options: FilterOptionOrDisabled[];
  selectedOptions: FilterOption[];
  setSelectedOptions: (option: FilterOption[]) => void;
  isDefaultOpen?: boolean;
  canSearch?: boolean;
  // Explicit selections distinguish all named options from no restriction.
  selectionMode?: "explicit";

  /**
   * When used in a sentence, title will be lowercased unless this is true.
   * @default true
   */
  preserveTitleCase?: boolean;
}

export const CollapsableFilter: FC<CollapsableFilterProps> = ({
  filterKey,
  title,
  options: allOptions,
  setSelectedOptions,
  selectedOptions,
  preserveTitleCase = false,
  isDefaultOpen = false,
  canSearch = true,
  selectionMode,
}) => {
  const options = allOptions.filter((o): o is FilterOption => !o.disabled);
  const disabledOptions = allOptions.filter(
    (o): o is DisabledFilterOption => o.disabled === true,
  );
  const [isOpen, setIsOpen] = useState(
    isDefaultOpen ?? options.length !== selectedOptions.length,
  );
  const [search, setSearch] = useState<string>("");
  const optionsScrollFade = useScrollFade(24, "both");

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const prevParamRef = useRef<string | null>(null);

  // Automatically uncollapse the relevant filter when the URL search param
  // changes. It should collapse all non-relevant filters if they were
  // previously open.
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!filterKey) {
      return;
    }

    const currentParam = searchParams.get(filterKey);
    if (currentParam === prevParamRef.current) {
      // This filter's param didn't change; ignore unrelated URL changes
      return;
    }

    const hasValues = parseCommaList(currentParam).length > 0;

    if (hasValues && !isOpen) {
      setIsOpen(true);
    }

    // Optionally auto-close only if this filter just lost all values
    if (!hasValues && isOpen && prevParamRef.current) {
      setIsOpen(false);
    }

    prevParamRef.current = currentParam;
  }, [filterKey, location.search, isOpen, searchParams]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const scrollToActive = useCallback(
    (node) => {
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    },
    [isOpen],
  );

  const matchesSearch = (o: FilterOptionOrDisabled) => {
    if (!search) return true;
    const s = search.toLowerCase();
    if (o.label && String(o.label).toLowerCase().includes(s)) return true;
    if (!o.disabled && o.searchValue?.toLowerCase().includes(s)) return true;
    if (o.id && String(o.id).toLowerCase().includes(s)) return true;
    return false;
  };

  const filteredOptions = orderBy(
    options?.filter(matchesSearch) || [],
    ["order", "count", "label"],
    ["asc", "desc", "asc"],
  );

  const filteredDisabledOptions = orderBy(
    disabledOptions.filter(matchesSearch),
    ["order", "label"],
    ["asc", "asc"],
  );

  const onCheckboxClick = (e, isChecked: boolean, option: FilterOption) => {
    e.preventDefault();
    e.stopPropagation();
    if (isChecked) {
      setSelectedOptions(selectedOptions.filter((o) => o.id !== option.id));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  };

  return (
    <Column ref={isOpen ? scrollToActive : undefined}>
      <Row
        align="center"
        borderRadius="xl"
        borderWidth="hairline"
        borderStyle="solid"
        borderColor="transparent"
        as="button"
        type="button"
        px={2}
        gap={1}
        py={1}
        height="32px"
        width="100%"
        _active={{ outline: "none" }}
        _focusVisible={{ outline: "none", boxShadow: "outline" }}
        _hover={{
          cursor: "pointer",
          bg: "base.lightBackground",
          borderColor: "base.border",
          boxShadow: "buttonShadowLight",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <CollapseChevron isOpen={isOpen} color="text.tertiary" />
        <Row alignItems="center" fontWeight="medium" gap={2}>
          <Box as={Text} size="sm" color="text.primary">
            {title}
          </Box>
          {selectedOptions.length > 0 &&
            !isUnrestrictedSelection({
              selectedOptions,
              options,
              selectionMode,
            }) && (
              <Badge variant="info" size="sm">
                {selectedOptions.length}
              </Badge>
            )}
        </Row>
      </Row>

      {isOpen && (
        <Column>
          <Column px={2} pt={1}>
            {canSearch && (
              <SearchInput
                placeholder={`Filter ${preserveTitleCase ? title : title.toLowerCase()}…`}
                size="sm"
                value={search ?? ""}
                width="auto"
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            )}
          </Column>
          {filteredOptions.length === 0 &&
          filteredDisabledOptions.length === 0 ? (
            <Row ml={8} my={2}>
              <Text color="text.secondary" size="sm">
                No options
              </Text>
            </Row>
          ) : (
            // Border on the wrapper so the fade mask doesn't fade it out
            <Column
              borderBottomWidth="hairline"
              borderBottomStyle="solid"
              borderColor="base.border"
              mb={2}
            >
              <Column
                ref={optionsScrollFade.ref}
                maxH="300px"
                overflowX="hidden"
                overflowY="auto"
                py={2}
                sx={optionsScrollFade.sx}
              >
                {filteredOptions.map((option) => {
                  const isChecked = Boolean(
                    selectedOptions.find((o) => o.id === option.id),
                  );
                  const isOnlySelection =
                    selectedOptions.length === 1 &&
                    selectedOptions[0]?.id === option.id;
                  return (
                    <FilterRow
                      count={option.filteredCount ?? option.count}
                      key={option.id}
                      isAll={isOnlySelection}
                      isChecked={isChecked}
                      option={option}
                      onCheckboxClick={(e) =>
                        onCheckboxClick(e, isChecked, option)
                      }
                      onRowClick={() => {
                        if (!isOnlySelection) {
                          setSelectedOptions([option]);
                          return;
                        }
                        // Back to unrestricted, which each mode spells
                        // differently: everything selected, or nothing.
                        setSelectedOptions(
                          selectionMode === "explicit" ? [] : options,
                        );
                      }}
                    />
                  );
                })}
                {filteredDisabledOptions.map((option) => (
                  <Row
                    key={option.label}
                    pl={3}
                    pr={2}
                    py={1}
                    width="100%"
                    alignItems="center"
                    gap={2}
                    sx={{
                      "label > span": {
                        width: "16px",
                        height: "16px",
                        svg: { strokeWidth: "1.5px !important" },
                      },
                      "& > div": { display: "flex" },
                    }}
                  >
                    <Checkbox isChecked={false} isDisabled onChange={noop} />
                    <Text size="sm" color="text.placeholder">
                      {option.label}
                    </Text>
                    {option.badge && (
                      <Badge variant="success" size="sm">
                        {option.badge}
                      </Badge>
                    )}
                  </Row>
                ))}
              </Column>
            </Column>
          )}
        </Column>
      )}
    </Column>
  );
};

type FilterRowProps = {
  onCheckboxClick: (e: React.MouseEvent<HTMLElement>) => void;
  onRowClick: () => void;
  isChecked: boolean;
  option: FilterOption;
  isAll: boolean;
  count?: number;
};

const FilterRow: FC<FilterRowProps> = ({
  onCheckboxClick,
  onRowClick,
  option,
  isChecked,
  isAll,
  count,
}) => {
  const [isCheckboxHovered, setIsCheckboxHovered] = useState(false);
  const [isCheckboxFocused, setIsCheckboxFocused] = useState(false);
  const isCheckboxTargeted = isCheckboxHovered || isCheckboxFocused;

  return (
    <Row
      key={option.id}
      className="group"
      borderRadius="md"
      display="grid"
      gridTemplateColumns="minmax(0, 1fr) auto"
      pos="relative"
      pr={2}
      pl={3}
      py={1}
      minHeight={isPresent(count) ? "32px" : undefined}
      width="100%"
      _hover={{
        bg: "base.background",
        cursor: "pointer",
      }}
      // Intentional sx: no pseudo prop for :has(:focus-visible)
      sx={{
        "&:has(:focus-visible)": {
          bg: "base.background",
          ".action-button": {
            opacity: 1,
          },
          ".action-count": {
            opacity: 0,
          },
        },
        // The row-wide button performs the trailing action, so ring that action rather
        // than the whole row. `button` is the row button; the checkbox is an input.
        "&:has(button:focus-visible) .action-button": {
          borderRadius: "sm",
          boxShadow: "outline",
        },
      }}
    >
      <Row
        alignItems="center"
        flex={1}
        gap={2}
        minWidth={0}
        sx={{
          "label > span": {
            width: "16px",
            height: "16px",
            svg: { strokeWidth: "1.5px !important" },
          },
          // Lift the checkbox above the row button's ::after
          "& > div": { display: "flex", position: "relative", zIndex: 1 },
        }}
      >
        <Checkbox
          aria-label={option.label ?? option.id}
          isChecked={isChecked}
          onChange={noop}
          onClick={onCheckboxClick}
          onMouseEnter={() => {
            setIsCheckboxHovered(true);
          }}
          onMouseLeave={() => {
            setIsCheckboxHovered(false);
          }}
          onFocus={() => {
            setIsCheckboxFocused(true);
          }}
          onBlur={() => {
            setIsCheckboxFocused(false);
          }}
        />
        <RowButton
          overlayRadius="md"
          flex={1}
          minWidth={0}
          overflow="hidden"
          // Ring lives on the trailing action instead; a row-wide ring overstates what this does.
          _focusVisible={{ outline: "none" }}
          onClick={(event) => {
            event.stopPropagation();
            onRowClick();
          }}
        >
          {/* Lift above the row button's ::after so the tooltip still gets hover */}
          <Row
            flex={1}
            minWidth={0}
            overflow="hidden"
            gap={2}
            alignItems="center"
            position="relative"
            zIndex={1}
          >
            {option.badge ?? (
              <>
                {option.icon}
                <TextWithTooltip size="sm">{option.label}</TextWithTooltip>
              </>
            )}
          </Row>
        </RowButton>
      </Row>
      {isPresent(count) && (
        <Row
          as={Text}
          size="sm"
          className="action-count"
          align="center"
          display="flex"
          gridArea="1 / 2"
          justifySelf="end"
          pl={2}
          color="text.secondary"
          // The hover fade stacks this above the row button's overlay, where it would swallow clicks on "Only" and "All".
          pointerEvents="none"
          _groupHover={{
            opacity: 0,
          }}
        >
          {commaNumber(count ?? 0)}
        </Row>
      )}
      {/* Out of flow so idle labels keep the full row width; the solid bg masks the label tail on hover */}
      <Row
        className="action-button"
        align="center"
        bg="base.background"
        display="flex"
        pl={2}
        opacity={0}
        pointerEvents="none"
        position="absolute"
        right={2}
        top="50%"
        transform="translateY(-50%)"
        // Above the label's zIndex 1 lift so it masks the label tail
        zIndex={2}
        _groupHover={{
          opacity: 1,
        }}
      >
        <Text color="text.secondary" size="sm">
          {isCheckboxTargeted ? "Toggle" : isAll ? "All" : "Only"}
        </Text>
      </Row>
    </Row>
  );
};

type Filter = Pick<
  CollapsableFilterProps,
  | "options"
  | "title"
  | "canSearch"
  | "isDefaultOpen"
  | "preserveTitleCase"
  | "selectionMode"
> & {
  // Sidebar heading this dimension renders under, after the "Filter by" block.
  // Presentation only: URL params and saved views never see it.
  section?: string;
};

export const modelQueryTypeFilterConfig = (
  models: ModelFiltersQuery["segments"],
): FilterOption[] => {
  const items = models
    .filter((s) => !!s.query_type)
    .map(({ query_type }) => ({
      id: query_type!,
      label: QueryTypeDictionary[query_type!],
    }));

  return uniqBy(items, "id");
};

export const syncStatusFilterConfig = (): FilterOption[] => [
  { id: "active", label: "Active" },
  { id: "disabled", label: "Disabled" },
  { id: "never-run", label: "Never run" },
];

export const lastRunStatusFilterConfig = (): FilterOption[] => {
  const syncStatusFilters: string[] = [
    SyncRunStatus.DISABLED,
    SyncRunStatus.SUCCESS,
    SyncRunStatus.FAILED,
    SyncRunStatus.WARNING,
    SyncRunStatus.PROCESSING,
    SyncRunStatus.QUERYING,
    SyncRunStatus.PENDING,
    SyncRunStatus.CANCELED,
  ];

  return syncStatusFilters.map((status) => ({
    id: status,
    label: SyncStatusToText[status],
    badge: (
      <Box maxWidth={20} sx={{ span: { fontSize: "11px" } }}>
        <SyncRunStatusBadge status={status} />
      </Box>
    ),
  }));
};

export const syncAlertsFilterConfig = [
  { id: SyncHealthStatus.Healthy, label: "No alerts", order: 0 },
  { id: SyncHealthStatus.Warning, label: "Warning alerts", order: 1 },
  { id: SyncHealthStatus.Unhealthy, label: "Critical alerts", order: 2 },
] as const satisfies FilterOption[];

export enum SyncProgressFilter {
  Running = "running",
  NotRunning = "not-running",
}

export const syncProgressFilterConfig = (): FilterOption[] => {
  return [
    { id: SyncProgressFilter.Running, label: "Running now", order: 0 },
    { id: SyncProgressFilter.NotRunning, label: "Not running", order: 1 },
  ];
};

/** A rail row that is not a server row: selectable, never an update target. */
export type BuiltInView = { id: string; name: string; view: ViewFilter[] };

const DEFAULT_VIEW: BuiltInView = { id: "default", name: "Default", view: [] };

const CURRENTLY_RUNNING_SYNCS_VIEW: BuiltInView = {
  id: "currently-running-syncs",
  name: "Currently running syncs",
  // FilterType never grew the real dimension keys, so the cast matches the ones
  // the hook makes when it builds the same shape from filter keys.
  view: [
    {
      type: "progress" as FilterType,
      operator: FilterOperator.Includes,
      value: [SyncProgressFilter.Running],
    },
  ],
};

/**
 * The rows `Filters` prepends to the server views. `useFilters` resolves
 * `?view=` ids against the same list, so a built-in restores from a URL instead
 * of being dropped as unknown. It is never restored from storage.
 */
export const builtInViewsFor = (resource?: FilterResource): BuiltInView[] =>
  resource === "sync"
    ? [DEFAULT_VIEW, CURRENTLY_RUNNING_SYNCS_VIEW]
    : [DEFAULT_VIEW];

export const createdByFilterConfig = (
  items: {
    created_by?: string;
    created_by_user: { id: string; name: string } | null;
  }[],
): FilterOption[] => {
  const res = items
    .filter((item) => !!(item.created_by || item.created_by_user))
    // created_by and users.id are bigints, which arrive as a number or a string
    // depending on the caller. Matchers compare ids as strings, so coerce here
    // rather than leaving each surface to guess.
    .map((item) => ({
      id: String(item.created_by_user?.id || item.created_by || ""),
      label: item.created_by_user?.name || "Unknown",
    }));

  return uniqBy(res, "id");
};

export const labelFilterConfig = (
  items: (
    | { tags: Record<string, string> }
    | { segment: { tags: Record<string, string> } | null }
  )[],
): FilterOption[] => {
  const allLabels: Record<string, string>[] = [];
  items.forEach((item) => {
    const entries = "tags" in item ? item.tags : (item.segment?.tags ?? {});
    Object.entries(entries).forEach(([key, value]) => {
      const entry = {};
      entry[key] = value;
      allLabels.push(entry);
    });
  });
  const res = allLabels.map((entry) => {
    const obj = Object.entries(entry)[0]!;
    return {
      id: [obj[0], obj[1]].join(":"),
      label: `${obj[0]}: ${obj[1]}`,
    };
  });

  return [...uniqBy(res, "id"), { id: "-1", label: "No labels" }];
};

export const sourceFilterConfig = (
  syncs: {
    segment: {
      connection: {
        id?: string;
        name?: string;
        definition?: {
          name?: string;
          icon?: string;
        };
      } | null;
    } | null;
  }[],
): FilterOption[] => {
  const items = syncs
    ?.filter(({ segment }) => !!segment?.connection?.id)
    .map(({ segment }) => ({
      id: segment?.connection?.id || "",
      label: segment?.connection?.name,
      searchValue: segment?.connection?.definition?.name,
      icon: segment?.connection?.definition?.name && (
        <HtImage
          alt={segment?.connection?.definition?.name || ""}
          flexShrink={0}
          maxHeight="100%"
          objectFit="contain"
          src={segment?.connection?.definition?.icon || ""}
          width="18px"
        />
      ),
    }));

  return uniqBy(items, "id");
};

export const journeyNameFilterConfig = (
  syncs: {
    segment: {
      name?: string;
      query_type: string | null;
      connection: {
        id?: string;
      } | null;
    } | null;
  }[],
): FilterOption[] => {
  const items = syncs
    ?.filter(({ segment }) => segment?.query_type === QueryType.JourneyNode)
    .map(({ segment }) => {
      // Journeys sync segments are always formatted as "Journey Name: JourneyNode Name"
      const journeyName = segment?.name?.split(":")[0] || "";
      return { id: journeyName, label: journeyName };
    });

  return uniqBy(items, "id");
};

export const sourceTypeFilterConfigForModels = (
  connections: {
    connection: {
      type?: string;
      id?: string;
      name?: string;
      definition?: {
        name?: string;
        icon?: string;
      };
    } | null;
  }[],
): FilterOption[] => {
  const namesByType = new Map<string, string>();
  for (const { connection } of connections) {
    if (!connection?.type || !connection.name) continue;
    const names = namesByType.get(connection.type);
    namesByType.set(
      connection.type,
      names ? `${names} ${connection.name}` : connection.name,
    );
  }

  const items = connections.map(({ connection }) => ({
    id: connection?.type || "",
    label: connection?.definition?.name,
    searchValue: namesByType.get(connection?.type || ""),
    icon: connection?.definition?.name && (
      <HtImage
        alt={connection?.definition?.name || ""}
        flexShrink={0}
        maxHeight="100%"
        objectFit="contain"
        src={connection?.definition?.icon || ""}
        width="18px"
      />
    ),
  }));

  return uniqBy(items, "id");
};

export const sourceTypeFilterConfig = (
  connections: {
    type?: string;
    id?: string;
    name?: string;
    definition?: {
      name?: string;
      icon?: string;
    };
  }[],
): FilterOption[] => {
  const namesByType = new Map<string, string>();
  for (const connection of connections) {
    if (!connection?.type || !connection.name) continue;
    const names = namesByType.get(connection.type);
    namesByType.set(
      connection.type,
      names ? `${names} ${connection.name}` : connection.name,
    );
  }

  const items = connections.map((connection) => {
    if (connection?.type === "sample-data") {
      return {
        id: connection.type,
        label: "Sample sources",
        searchValue: namesByType.get(connection.type),
        icon: (
          <Box
            as={SourceIcon}
            flexShrink={0}
            maxHeight="100%"
            objectFit="contain"
            fontSize="18px"
          />
        ),
      };
    }

    return {
      id: connection?.type || "",
      label: connection?.definition?.name,
      searchValue: namesByType.get(connection?.type || ""),
      icon: connection?.definition?.name && (
        <HtImage
          alt={connection?.definition?.name || ""}
          flexShrink={0}
          maxHeight="100%"
          objectFit="contain"
          src={connection?.definition?.icon || ""}
          width="18px"
        />
      ),
    };
  });

  return uniqBy(items, "id");
};

export const destinationFilterConfig = (
  syncs: {
    destination: {
      id?: string | null;
      name?: string | null;
      definition?: {
        icon?: string;
        name?: string;
      };
    } | null;
  }[],
): FilterOption[] => {
  const items = syncs.map(({ destination }) => {
    const { id, name, definition } = destination || {};

    return {
      id: id || "",
      label: name || definition?.name,
      searchValue: definition?.name,
      icon: (
        <HtImage
          alt={definition?.name || ""}
          flexShrink={0}
          maxHeight="100%"
          objectFit="contain"
          src={definition?.icon || ""}
          width="18px"
          height="18px"
        />
      ),
    };
  });

  return uniqBy(items, "id");
};

export const destinationTypeFilterConfig = (
  destinations: {
    name?: string | null;
    type?: string | null;
    definition?: {
      icon?: string;
      name?: string;
    };
  }[],
): FilterOption[] => {
  const namesByType = new Map<string, string>();
  for (const { type, name } of destinations) {
    if (!type || !name) continue;
    const names = namesByType.get(type);
    namesByType.set(type, names ? `${names} ${name}` : name);
  }

  const items = destinations.map((destination) => {
    const { type, name, definition } = destination!;

    return {
      id: type || "",
      label: definition?.name ?? name ?? "",
      searchValue: namesByType.get(type || ""),
      icon: (
        <HtImage
          alt={definition?.name || ""}
          flexShrink={0}
          maxHeight="100%"
          objectFit="contain"
          src={definition?.icon || ""}
          width="18px"
        />
      ),
    };
  });

  return uniqBy(items, "id");
};

export const privacyLevelFilterOptions: FilterOption[] = [
  { id: "unreviewed", label: "Unreviewed" },
  { id: PrivacyLevel.REDACTED, label: "Redacted" },
  { id: PrivacyLevel.BLOCKED, label: "Blocked" },
  { id: PrivacyLevel.APPROVED, label: "Approved" },
];

export const piiCategoryFilterOptions: FilterOption[] = piiCategories.map(
  (category) => ({ id: category.toLowerCase(), label: category }),
);

export enum PiiReviewStatus {
  NeedsReview = "needs-review",
  NoIssue = "no-issue",
}

export const piiReviewFilterOptions: FilterOption[] = [
  { id: PiiReviewStatus.NeedsReview, label: "Needs review" },
  { id: PiiReviewStatus.NoIssue, label: "No issues found" },
];

export const parentModelFilterConfigForAudiences = (
  parents: {
    id: string;
    name: string;
    connection?: null | { definition?: { name: string; icon: string } };
  }[],
): FilterOption[] => {
  const items = parents.map((parent) => ({
    id: parent?.id,
    label: parent?.name,
    icon: (
      <IntegrationIcon
        size={5}
        name={parent.connection?.definition?.name}
        src={parent.connection?.definition?.icon}
      />
    ),
  }));

  return uniqBy(items, "id");
};

export const subsetsFilterConfigForAudiences = (
  subsets: { id: string; name: string; groupName: string }[],
): FilterOption[] => {
  const items = subsets.map((subset) => ({
    id: subset?.id,
    label: `${subset?.name} (${subset?.groupName})`,
  }));

  return uniqBy(orderBy(items, "groupName"), "id");
};

export const sourceConfigForAudiences = (
  parents: {
    id: string;
    name: string;
    connection?: null | {
      id: string;
      name: string;
      definition?: { name: string; icon: string };
    };
  }[],
): FilterOption[] => {
  const sources = parents.map((parent) => parent.connection).filter(isPresent);

  const items = sources.map((source) => ({
    id: source?.id,
    label: source?.name,
    icon: (
      <IntegrationIcon
        size={5}
        name={source?.definition?.name}
        src={source?.definition?.icon}
      />
    ),
  }));

  return uniqBy(items, "id");
};
